import { cn } from "@/lib/utils";
import { Facebook } from "lucide-react";
import type { ReactNode } from "react";

const PLATFORM_CONFIG: Record<string, { label: string; bg: string; text: string; border: string; iconBg: string }> = {
  meta: { label: "Meta", bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", iconBg: "bg-[#1877F2]" },
  tiktok: { label: "TikTok", bg: "bg-gray-50", text: "text-gray-900", border: "border-gray-300", iconBg: "bg-[#000000]" },
  google: { label: "Google", bg: "bg-green-50", text: "text-green-700", border: "border-green-200", iconBg: "bg-[#34A853]" },
  unified: { label: "All Platforms", bg: "bg-primary/5", text: "text-primary", border: "border-primary/20", iconBg: "bg-primary" },
};

function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.6a8.2 8.2 0 0 0 4.76 1.52V6.69h-1z"/>
    </svg>
  );
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

function getIconForPlatform(platform: string, iconSize: string): ReactNode | null {
  switch (platform) {
    case "meta":
      return <Facebook className={iconSize} />;
    case "tiktok":
      return <TikTokIcon className={iconSize} />;
    case "google":
      return <GoogleIcon className={iconSize} />;
    default:
      return null;
  }
}

const MUTED_CONFIG = {
  bg: "bg-gray-100",
  text: "text-gray-500",
  border: "border-gray-200",
  iconBg: "bg-gray-400",
};

interface PlatformBadgeProps {
  platform: string;
  size?: "xs" | "sm" | "md" | "lg";
  showIcon?: boolean;
  className?: string;
  variant?: "default" | "muted";
}

export function PlatformBadge({ platform, size = "sm", showIcon = true, className, variant = "default" }: PlatformBadgeProps) {
  const defaultConfig = PLATFORM_CONFIG[platform] || PLATFORM_CONFIG.unified;
  const config = variant === "muted" ? { ...MUTED_CONFIG, label: defaultConfig.label } : defaultConfig;
  const iconSize = size === "xs" ? "w-2 h-2" : size === "sm" ? "w-2.5 h-2.5" : size === "md" ? "w-3 h-3" : "w-3.5 h-3.5";
  const icon = showIcon ? getIconForPlatform(platform, iconSize) : null;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded font-medium border",
        size === "xs" ? "px-1 py-px text-[9px]" : size === "sm" ? "px-1.5 py-0.5 text-[10px]" : size === "md" ? "px-2 py-0.5 text-xs" : "px-2.5 py-1 text-sm",
        config.bg,
        config.text,
        config.border,
        className
      )}
      data-testid={`platform-badge-${platform}`}
    >
      {icon && (
        <span className={cn(
          "inline-flex items-center justify-center rounded-full text-white flex-shrink-0",
          size === "xs" ? "w-3 h-3" : size === "sm" ? "w-3.5 h-3.5" : size === "md" ? "w-4 h-4" : "w-5 h-5",
          config.iconBg
        )}>
          {icon}
        </span>
      )}
      {config.label}
    </span>
  );
}

interface PlatformIconProps {
  platform: string;
  size?: "sm" | "md" | "lg";
  className?: string;
}

export function PlatformIcon({ platform, size = "sm", className }: PlatformIconProps) {
  const config = PLATFORM_CONFIG[platform] || PLATFORM_CONFIG.unified;
  const iconSize = size === "sm" ? "w-2.5 h-2.5" : size === "md" ? "w-3 h-3" : "w-3.5 h-3.5";
  const icon = getIconForPlatform(platform, iconSize);
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center rounded-full text-white flex-shrink-0",
        size === "sm" ? "w-4 h-4" : size === "md" ? "w-5 h-5" : "w-6 h-6",
        config.iconBg,
        className
      )}
      title={config.label}
      data-testid={`platform-icon-${platform}`}
    >
      {icon || "?"}
    </span>
  );
}

interface PlatformDotProps {
  platform: string;
  className?: string;
}

export function PlatformDot({ platform, className }: PlatformDotProps) {
  const colors: Record<string, string> = {
    meta: "bg-blue-500",
    tiktok: "bg-[#000000]",
    google: "bg-green-500",
  };
  return (
    <span
      className={cn("inline-block w-2 h-2 rounded-full", colors[platform] || "bg-primary", className)}
      title={PLATFORM_CONFIG[platform]?.label || platform}
    />
  );
}

export function getPlatformColor(slug: string): string {
  const colors: Record<string, string> = {
    meta: "#1877F2",
    tiktok: "#000000",
    google: "#34A853",
  };
  return colors[slug] || "#6366F1";
}
